import { findChatById } from '@/lib/chatIndex';
import React, { useMemo, useRef } from 'react';
import { Platform, View } from 'react-native';
import PropTypes from 'prop-types';
import { usePathname, useRouter } from 'expo-router';
import { useUIStore, useLightboxStore, useModalStore, useToastStore, useAuthStore, useModelStore, useChatStore, useDeckStore } from '@/store';
import { useTheme } from '@/context/ThemeContext';
import Icon from '@/components/Icon';
import Text from '@/components/Typography/Text';
import { useTranslation } from '@/hooks/useTranslation';
import socketManager from '@/lib/socket';
import { chatsApi, coworkerApi } from '@/lib/api';
import { useCoworkerStore } from '@/store';
import { findTaskIdForConversation } from '@/store/coworker';
import { workBoundaryRequestId } from '@/lib/workTaskBoundary';
import useMessageAgents from '@/hooks/useMessageAgents';
import MessageBotAvatar from '@/components/Messages/MessageBotAvatar';
import MessageBubble from '@/components/Messages/MessageBubble';
import MessageSlim from '@/components/Messages/MessageSlim';
import MessageWide from '@/components/Messages/MessageWide';
import DOMPurify from 'isomorphic-dompurify';
import slugify from '@/constants/utils/slugify';
import copyText from '@/constants/utils/copy_text';
import markdownToPlainText from '@/constants/utils/markdown_to_plain_text';
import getMessageHtmlForCopy from '@/constants/utils/get_message_html_for_copy';
import { downloadMedia, isIOSSafari } from '@/constants/utils/mediaDownload';
import { getActiveEditorInsert } from '@/lib/editorInsertRegistry';
import {
  getMessageEditorInsertMarkdown,
  getMessageSpreadsheetRows,
} from '@/constants/utils/message_editor_insert';
import { canUseDesktopEditors, createAndOpenEditor } from '@/constants/utils/files';
import CoworkerArtifacts from '@/components/Coworker/CoworkerArtifacts';
import { artifactsNotCoveredByMessage, isHtmlArtifact } from '@/components/Coworker/coworkerArtifactTypes';
import WorkTurnActions from '@/components/Coworker/WorkTurnActions';
import CoworkerDocumentPreview from '@/components/Coworker/CoworkerDocumentPreview';
import CoworkerHtmlPreview from '@/components/Coworker/CoworkerHtmlPreview';
import { isHtmlCodeFile } from '@/constants/utils/code_file';
import { useModalHelper } from '@/hooks/useModalHelper';
import { MESSAGE_REGENERATE_TYPES, RESPONSE_ACTION_TYPES } from '@/constants/config';
import { findParentUserMessage, getMessageDisplayInfo, isAutoModeAttribution, resolveAttributionModelIds } from '@/constants/utils/message';
import { MESSAGE_WIDTH, MESSAGE_WIDTH_AMOUNTS } from '@/constants/config';
import { stripWorkInjectedPrompt } from '@/constants/work';

// Mirrors MessageBubble's centered column so the coworker artifact fallback
// cards sit in the same container as the conversation, not the page edge.
const COWORKER_ARTIFACTS_CONTAINER_STYLE = (messageWidth) => ({
  width: '100%',
  minWidth: 0,
  maxWidth: messageWidth === MESSAGE_WIDTH.NARROW
    ? MESSAGE_WIDTH_AMOUNTS.NARROW
    : messageWidth === MESSAGE_WIDTH.MEDIUM
      ? MESSAGE_WIDTH_AMOUNTS.MEDIUM
      : '100%',
  alignSelf: 'center',
  paddingHorizontal: 16,
});

// Constants
const MESSAGE_LAYOUTS = {
  bubble: MessageBubble,
  slim: MessageSlim,
  wide: MessageWide,
};

const ADDED_TO_EDITOR_TOAST_KEYS = {
  document: 'messages.addToEditor.addedToDocument',
  spreadsheet: 'messages.addToEditor.addedToSpreadsheet',
  presentation: 'messages.addToEditor.addedToPresentation',
};

function Message({
  message,
  conversationId,
  isOwner,
  chatTitle,
  isLastMessage = false,
  isInMessageGroup = false,
  isLastGroupInChat = false,
}) {
  const { t } = useTranslation();
  const { colors } = useTheme();
  const router = useRouter();
  const pathname = usePathname();
  const { addToast } = useToastStore(state => state.actions);
  const { openModal, closeModal } = useModalStore(state => state.actions);
  const { useConfirmationModal } = useModalHelper();
  const showConfirmationModal = useConfirmationModal();
  const { openLightbox } = useLightboxStore(state => state.actions);
  const messageLayout = useUIStore(state => state.messageLayout);
  const messageWidth = useUIStore(state => state.messageWidth);
  const showMessageActions = useUIStore(state => state.showMessageActions);
  const isGhost = useAuthStore(state => state.user?.flags?.isGhost);
  const userId = useAuthStore(state => state.user?.id);
  const getDeepModelById = useModelStore(state => state.getDeepModelById);
  const { agentIds: effectiveAgentIds, agents } = useMessageAgents(conversationId, message);

  const getPlainTextContent = useMemo(() => {
    const raw = typeof message.content === 'string' ? message.content : String(message.content || '');
    if (!raw) return '';

    if (Platform.OS === 'web') {
      try {
        return DOMPurify.sanitize(raw, {
          ALLOWED_TAGS: [],
          ALLOWED_ATTR: [],
        }).trim();
      } catch (_) {
        // Fall through to regex-based stripping if sanitization fails.
      }
    }

    return raw.replace(/<[^>]*>/g, '').trim();
  }, [message.content]);

  // Memoize message states with specific dependencies
  const messageWithStates = useMemo(() => ({
    ...message,
    isWaiting: message.isWaiting || false,
    isTyping: message.isTyping || false,
    isThinking: message.isThinking || false,
    isStreaming: message.isStreaming || false,
    isError: isGhost ? false : (message.isError || false), // Ghost messages are never errors
    hasError: isGhost ? false : (message.isError || false), // Ghost messages are never errors
    buttons: message.buttons || [],
    citations: message.citations || [],
    products: message.products || [],
    stockQuotes: message.stockQuotes || [],
    places: message.places || [],
    map: message.map || null,
    isRequestingMedia: message.isRequestingMedia || null,
    modelIds: message.modelIds || [],
    agentIds: effectiveAgentIds,
    files: message.files || [],
    inferences: message.inferences || [],
    isSaved: message.flags?.isSaved || false,
    isEdited: message.isEdited || false,
    isQueued: message.isQueued || false,
  }), [message, isGhost, effectiveAgentIds]);

  const botAvatar = useMemo(() => agents[0] ? <MessageBotAvatar agent={agents[0]} /> : null, [agents]);

  // Resolve the parent user turn for auto-mode / political-override attribution.
  const parentUserMessage = useChatStore((state) => {
    if (message.role === 'user') return null;
    const chat = findChatById(state.chats, conversationId);
    return findParentUserMessage(chat?.messages, message);
  });

  // Coworker thread link — inline artifact cards resolve presigned
  // downloads through the task API, not the File/CDN path.
  const coworkerTaskId = useChatStore((state) => {
    const chat = findChatById(state.chats, conversationId);
    return chat?.coworkerSession || null;
  });

  // Auto attribution is persisted per turn. Never use the live composer here:
  // changing the next message to Auto must not rewrite historical footers.
  const isAutoMode = isAutoModeAttribution(parentUserMessage, message);

  // Political / intent-engine overrides: message.modelIds become the routed
  // model (e.g. Gemini) while ___displayModelIds keeps the user selection
  // (e.g. Arya) for attribution in the footer.
  const displayModelIds = useMemo(
    () => resolveAttributionModelIds(message, parentUserMessage, isAutoMode),
    [isAutoMode, message, parentUserMessage],
  );

  const displayModels = useMemo(() => {
    if (!displayModelIds.length) return [];
    return displayModelIds.map(id => getDeepModelById(id)).filter(Boolean);
  }, [displayModelIds, getDeepModelById]);

  // Get the appropriate message component - memoize this too
  const MessageComponent = useMemo(() => 
    MESSAGE_LAYOUTS[messageLayout] || MessageBubble
  , [messageLayout]);

  // Use refs for values that change during streaming to avoid recreating handlers every 50ms
  const contentRef = useRef(message.content);
  contentRef.current = message.content;
  const imagesRef = useRef(message.images);
  imagesRef.current = message.images;
  const filesRef = useRef(message.files);
  filesRef.current = message.files;

  // Memoize all handlers together
  const handlers = useMemo(() => ({
    onOpenLightbox: (options) => {
      const { imageUrl, fileId } = options || {};
      if (!imageUrl || !fileId) return;
      openLightbox({
        ...options,
        conversationId,
      });
    },
    onOpenFileEditor: (fileId) => {
      if (!fileId) return;
      openModal('FileEditModal', {
        fileId: fileId,
      });
    },
    onCopy: async () => {
      try {
        // Dual-payload copy: stripped plain text for plain destinations
        // (Notes, email, terminals) so they don't see raw ## or ** markers,
        // plus sanitized rendered HTML for rich destinations (Word, Google
        // Docs) and markdown editors that auto-convert HTML (Obsidian).
        // Work user rows may still hold a launch/steer exec envelope;
        // markdown-to-plain-text would keep the inner catalog text.
        const source = message.role === 'user'
          ? stripWorkInjectedPrompt(contentRef.current)
          : contentRef.current;
        const plainText = markdownToPlainText(source);
        const cleanHtml = getMessageHtmlForCopy(message.id, source);
        await copyText(plainText, cleanHtml);
        addToast(t('messages.copied'), 'success');
      } catch (error) {
        console.error('Failed to copy message:', error);
        addToast(t('messages.copyFailed'), 'error');
      }
    },
    // Append this response to the editor open beside the chat. Text plus any
    // generated/attached images (as ![alt](url) Markdown) are handed over so
    // image-only replies can still use "Add to document".
    onAddToEditor: () => {
      const target = getActiveEditorInsert();
      if (!target) return;
      const payload = getMessageEditorInsertMarkdown({
        content: contentRef.current,
        files: filesRef.current,
        images: imagesRef.current,
      });
      if (!payload) {
        addToast(t('messages.addToEditor.empty'), 'error');
        return;
      }
      target.insert(payload);
      addToast(
        t(ADDED_TO_EDITOR_TOAST_KEYS[target.noun] || 'messages.addToEditor.addedFallback'),
        'success',
      );
    },
    // Desktop-web three-dot menu: open a document editor (or append if one is
    // already open) and seed it with this reply.
    onAddToDocument: async () => {
      const payload = getMessageEditorInsertMarkdown({
        content: contentRef.current,
        files: filesRef.current,
        images: imagesRef.current,
      });
      if (!payload) {
        addToast(t('messages.addToEditor.empty'), 'error');
        return;
      }
      const target = getActiveEditorInsert();
      if (target?.noun === 'document') {
        target.insert(payload);
        addToast(t('messages.addToEditor.addedToDocument'), 'success');
        return;
      }
      if (!canUseDesktopEditors()) {
        addToast(t('messages.addToEditor.desktopOnly'), 'error');
        return;
      }
      try {
        await createAndOpenEditor('document', {
          markdown: payload,
          conversationId,
          messageId: message.id,
        });
        addToast(t('messages.addToEditor.openedDocument'), 'success');
      } catch (error) {
        console.error('Failed to open document editor:', error);
        addToast(t('messages.addToEditor.openFailed'), 'error');
      }
    },
    // Desktop-web: open a spreadsheet (or append if one is open) seeded from
    // the first Markdown table in this reply.
    onAddToSpreadsheet: async () => {
      const rows = getMessageSpreadsheetRows({ content: contentRef.current });
      if (!rows.length) {
        addToast(t('messages.addToEditor.emptyTable'), 'error');
        return;
      }
      const target = getActiveEditorInsert();
      if (target?.noun === 'spreadsheet') {
        const tsv = rows.map(row => row.join('\t')).join('\n');
        target.insert(tsv);
        addToast(t('messages.addToEditor.addedToSpreadsheet'), 'success');
        return;
      }
      if (!canUseDesktopEditors()) {
        addToast(t('messages.addToEditor.desktopOnly'), 'error');
        return;
      }
      try {
        await createAndOpenEditor('spreadsheet', {
          rows,
          conversationId,
          messageId: message.id,
        });
        addToast(t('messages.addToEditor.openedSpreadsheet'), 'success');
      } catch (error) {
        console.error('Failed to open spreadsheet editor:', error);
        addToast(t('messages.addToEditor.openFailed'), 'error');
      }
    },
    onShare: !isOwner ? null : () => {
      openModal('ShareModal', {
        onClose: () => {},
        conversationId,
        chatTitle,
      });
    },
    onSpeak: () => {
      const source = message.role === 'user'
        ? stripWorkInjectedPrompt(contentRef.current || '')
        : (contentRef.current || '');
      const plainText = markdownToPlainText(source);
      if (!plainText?.trim()) {
        addToast(t('messages.speak.empty'), 'error');
        return;
      }
      if (isGhost) {
        openModal('GhostSignupModal');
        return;
      }
      openModal('MessageSpeakModal', { text: plainText.trim() });
    },
    onRegenerate: !isOwner ? null : (regenerateType = MESSAGE_REGENERATE_TYPES.AGAIN) => {
      if (!message.id) {
        console.warn('Cannot regenerate message without ID', message);
        return;
      }
      if (!socketManager.isSocketConnected()) {
        addToast(t('messages.connectionError'), 'error', [], { category: 'connection' });
        return;
      }
      if (isGhost) {
        openModal('GhostSignupModal');
        return;
      }

      // messageId must be the assistant message id so the backend can remove it and re-run inference (including image generation in auto mode).
      socketManager.emitRegenerateMessage({
        userId,
        conversationId,
        messageId: message.id,
        regenerateType: regenerateType,
      });
    },
    onFeedback: !isOwner ? null : () => {
      const imageUrls = message.images?.length > 0 
        ? '\nImage URLs:\n' + message.images.map(img => img.url).join(', ')
        : '';
      const fileIds = message.files?.length > 0 
        ? '\nFile IDs:\n' + message.files.map(file => file.id).join(', ')
        : '';
      openModal('FeedbackModal', {
        isOpen: true,
        messageId: message.id,
        conversationId: conversationId,
        initialContent: (getPlainTextContent || '') + imageUrls + fileIds,
      });
    },
    onReport: () => {
      openModal('ReportModal', {
        isOpen: true,
        messageId: message.id,
        conversationId: conversationId,
        initialContent: getPlainTextContent || '',
      });
    },
    onBookmark: !isOwner ? null : async () => {
      try {
        if (!message.id) {
          console.warn('Cannot bookmark message without ID', message);
          return;
        }
        if (isGhost) {
          openModal('GhostSignupModal');
          return;
        }
        const isSaved = message?.flags?.isSaved === true;
        if (isSaved) {
          await useChatStore.getState().unsaveMessage(conversationId, message.id);
          addToast(t('messages.unsaved'), 'success');
        } else {
          await useChatStore.getState().saveMessage(conversationId, message.id);
          addToast(t('messages.saved'), 'success', [
            {
              title: t('messages.addToFolder'),
              icon: 'folder-plus',
              action: RESPONSE_ACTION_TYPES.ADD_TO_BOOKMARK_FOLDER,
              data: { conversationId, messageId: message.id },
            },
            {
              title: t('messages.viewBookmarks'),
              icon: 'bookmark',
              href: '/bookmarks',
            },
          ]);
        }
      } catch (error) {
        console.error('Failed to toggle bookmark:', error);
        addToast(t('messages.bookmarkFailed'), 'error');
      }
    },
    onStartNewTask: !isOwner || !coworkerTaskId ? null : async () => {
      const chat = findChatById(useChatStore.getState().chats, conversationId);
      const taskId = findTaskIdForConversation(conversationId, { chat, ...useCoworkerStore.getState() });
      if (!taskId) return;
      const parent = message.role === 'user' ? message : findParentUserMessage(chat?.messages || [], message);
      const prompt = stripWorkInjectedPrompt(parent?.content || message.content || '');
      if (!prompt.trim()) return;
      const fileIds = (parent?.files || []).map(file => String(file.id || file._id || file));
      const requestId = workBoundaryRequestId(taskId, prompt, fileIds);
      try {
        const out = await coworkerApi.prepareTaskBoundary(taskId, { message: prompt, fileIds,
          requestId, manual: true, sourceMessageId: parent?.id || parent?._id || message.id });
        useCoworkerStore.getState().setHandoffReview(taskId, out.routing);
        addToast('Review the new-task brief above the message box', 'success');
      } catch (err) { addToast(err?.message || 'Could not prepare the new task', 'error'); }
    },
    onFork: !isOwner || message.isArchivedMessage ? null : async (destination = 'side-by-side') => {
      if (!message.id) return;
      if (isGhost) {
        openModal('GhostSignupModal');
        return;
      }

      try {
        const forkedConversation = await chatsApi.forkConversation(conversationId, message.id);
        useChatStore.getState().addChat(forkedConversation);

        if (destination === 'side-by-side') {
          const deckActions = useDeckStore.getState().actions;

          if (pathname === '/deck') {
            const columnId = deckActions.addColumn({
              type: 'conversation',
              refId: forkedConversation.id,
              label: forkedConversation.title,
            });
            if (columnId) {
              deckActions.setFocusedColumnId(columnId);
              addToast(t('messages.forkedSideBySide'), 'success');
              return;
            }
          } else {
            const slug = await deckActions.createDeck({
              name: `${chatTitle || 'Conversation'} fork`,
              columns: [
                {
                  type: 'conversation',
                  ref: { kind: 'Conversation', id: conversationId },
                  label: chatTitle || 'Original',
                },
                {
                  type: 'conversation',
                  ref: { kind: 'Conversation', id: forkedConversation.id },
                  label: forkedConversation.title || 'Fork',
                },
              ],
            });
            if (slug) {
              router.push({ pathname: '/deck', params: { d: slug } });
              addToast(t('messages.forkedSideBySide'), 'success');
              return;
            }
          }

          addToast(t('messages.forkWorkspaceUnavailable'), 'error');
        }

        router.push(`/c/${forkedConversation.id}`);
      } catch (error) {
        console.error('Failed to fork conversation:', error);
        addToast(error.message || t('messages.forkFailed'), 'error');
      }
    },
    onEdit: !isOwner ? null : () => {
      if (!message.id || message.role !== 'user') return;
      if (isGhost) {
        openModal('GhostSignupModal');
        return;
      }
      if (typeof message.id === 'string' && message.id.startsWith('msg_')) {
        addToast(t('messages.waitForSending'), 'error');
        return;
      }
      openModal('EditMessageModal', {
        conversationId,
        messageId: message.id,
        initialContent: message.content || '',
      });
    },
    onDelete: !isOwner ? null : async () => {
      if (!message.id) return;
      showConfirmationModal({
        title: t('messages.deleteTitle'),
        message: t('messages.deleteMessage'),
        confirmText: t('common.delete'),
        cancelText: t('common.cancel'),
        isDangerous: true,
        options: {
          height: 200,
        },
        onConfirm: async () => {
          try {
            await useChatStore.getState().deleteMessage(conversationId, message.id);
            addToast(t('messages.deleted'), 'success');
          } catch (error) {
            console.error('Failed to delete message:', error);
            addToast(error.message || t('messages.deleteFailed'), 'error');
          }
        },
        onCancel: () => {
          console.log('Canceled');
          closeModal();
        },
      });
    },
    onExport: async () => {
      try {
        // If there are images and no content, check for expired flag
        if (Array.isArray(message.images) && message.images?.length > 0 && !message.content && message.images[0]?.flags?.isExpired === true) {
          addToast('Cannot export expired image', 'error');
          return;
        }
        if (Array.isArray(message.files) && message.files?.length > 0 && !message.content && message.files[0]?.isExpired === true) {
          addToast('Cannot export expired file', 'error');
          return;
        }

        // If there are images, download the first image
        if (Array.isArray(message.images) && message.images.length > 0) {
          const firstImage = message.images[0];
          const imageUrl = firstImage?.url || firstImage?.cdnPath || firstImage?.uri;
          if (!imageUrl) {
            addToast('No downloadable image URL found', 'error');
            return;
          }
          const filename = `${slugify(chatTitle || 'untitled-chat')}-image-${message.id || Date.now()}.png`;

          const result = await downloadMedia(imageUrl, filename, {
            onSuccess: (msg) => addToast(msg === 'Download complete' ? 'Image downloaded successfully' : msg, 'success'),
            onError: (msg) => addToast(msg || 'Failed to download image', 'error'),
          });

          // If iOS Safari needs fallback, show the instructions modal
          if (result?.needsFallback && Platform.OS === 'web' && isIOSSafari()) {
            openModal('SaveImageInstructionsModal', { imageUrl });
          }
        } else if (Array.isArray(message.files) && message.files.length > 0) {
          const firstFile = message.files[0];
          const fileUrl = firstFile?.cdnPath || firstFile?.url || firstFile?.uri;
          if (!fileUrl) {
            addToast('No downloadable file URL found', 'error');
            return;
          }
          const filename = `${slugify(chatTitle || 'untitled-chat')}-file-${message.id || Date.now()}.${firstFile.extension || 'bin'}`;

          const result = await downloadMedia(fileUrl, filename, {
            shareInstead: Platform.OS !== 'web', // Native: share files instead of saving to Photos
            onSuccess: (msg) => addToast(msg === 'Download complete' ? 'File downloaded successfully' : msg, 'success'),
            onError: (msg) => addToast(msg || 'Failed to download file', 'error'),
          });

          // iOS Safari can neither blob-download nor share most files; open the
          // file directly so the user can save it from Safari's own UI.
          if (result?.needsFallback && Platform.OS === 'web' && isIOSSafari()) {
            window.open(fileUrl, '_blank', 'noopener,noreferrer');
          }
        } else {
          // Text export - only on web for now
          if (Platform.OS !== 'web') {
            // On native, use share for text content
            const cleanContent = message.role === 'user'
              ? stripWorkInjectedPrompt(message.content || '')
              : (message.content || '');
            await copyText(cleanContent);
            addToast('Message copied to clipboard', 'success');
            return;
          }
          
          const exportSource = message.role === 'user'
            ? stripWorkInjectedPrompt(message.content || '')
            : (message.content || '');
          const cleanContent = DOMPurify.sanitize(exportSource, {
            ALLOWED_TAGS: [], 
            ALLOWED_ATTR: [],
          });
          const theChatTitle = chatTitle || 'untitled-chat';
          const filename = `${slugify(theChatTitle)}-${message.id || Date.now()}.txt`;
          const blob = new Blob([cleanContent], { type: 'text/plain' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = filename;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          addToast('Message exported as text file', 'success');
        }
      } catch (error) {
        console.error('Failed to export message:', error);
        addToast('Failed to export message', 'error');
      }
    },
    onPreferResponse: !isOwner ? null : async () => {
      if (!message.id) {
        console.warn('Cannot prefer message without ID', message);
        return;
      }
      try {
        await useChatStore.getState().setMessagePreference(conversationId, message.id, true);
        addToast(t('messages.responsePreferred'), 'success');
      } catch (error) {
        console.error('Failed to prefer response:', error);
        addToast(t('messages.responsePreferredFailed'), 'error');
      }
    },
    onRejectResponse: !isOwner ? null : async () => {
      if (!message.id) {
        console.warn('Cannot reject message without ID', message);
        return;
      }
      try {
        await useChatStore.getState().setMessagePreference(conversationId, message.id, false);
        addToast(t('messages.responseRejected'), 'success');
      } catch (error) {
        console.error('Failed to reject response:', error);
        addToast(t('messages.responseRejectFailed'), 'error');
      }
    },
  }), [message, getPlainTextContent, conversationId, coworkerTaskId, chatTitle, isOwner, openModal, closeModal, openLightbox, addToast, userId, isGhost, t, pathname, router, showConfirmationModal]);

  // Memoize accessibility props
  const accessibilityProps = useMemo(() => ({
    accessibilityLabel: `Message ${message.isError ? 'with error' : ''}`
  }), [message.isError]);

  const isUser = message.role === 'user';
  const { shouldDisplayMessage } = getMessageDisplayInfo(messageWithStates, isUser);

  if (!shouldDisplayMessage) return null;

  const leftoverArtifacts = artifactsNotCoveredByMessage(
    message.parameters?.coworkerArtifacts,
    { files: messageWithStates.files, images: messageWithStates.images },
  );
  const leftoverHtmlArtifacts = leftoverArtifacts.filter((artifact) => isHtmlArtifact(artifact));
  const leftoverOtherArtifacts = leftoverArtifacts.filter((artifact) => !isHtmlArtifact(artifact));
  const htmlFiles = (messageWithStates.files || []).filter((file) => isHtmlCodeFile(file));
  const workTaskId = coworkerTaskId && typeof coworkerTaskId === 'object'
    ? String(coworkerTaskId._id || coworkerTaskId.id || '')
    : String(coworkerTaskId || '');
  const bubbleMessage = !isUser && htmlFiles.length > 0
    ? {
      ...messageWithStates,
      files: (messageWithStates.files || []).filter((file) => !isHtmlCodeFile(file)),
    }
    : messageWithStates;

  return (
    <View>
      <MessageComponent
        conversationId={conversationId}
        message={bubbleMessage}
        agents={agents}
        botAvatar={botAvatar}
        models={displayModels}
        isAutoMode={isAutoMode}
        displayModelIds={displayModelIds}
        isLastMessage={isLastMessage}
        isInMessageGroup={isInMessageGroup}
        isLastGroupInChat={isLastGroupInChat}
        messageWidth={messageWidth}
        showMessageActions={showMessageActions}
        {...handlers}
        {...accessibilityProps}
      />
      {/* Coworker thread deliverables: rendered as first-class Files
          (materialized at settlement → standard MessageImages/MessageFiles
          above). PDFs get an inline document preview (web). HTML games and
          apps embed as a click-to-play frame instead of a file card. The
          presigned-URL cards below are the FALLBACK for rows not yet
          materialized (live view, oversized, or R2-missing). */}
      {!isUser && (coworkerTaskId || htmlFiles.length > 0) && (messageWithStates.files?.length || 0) > 0 ? (
        <View style={COWORKER_ARTIFACTS_CONTAINER_STYLE(messageWidth)}>
          {coworkerTaskId && <CoworkerDocumentPreview files={messageWithStates.files} />}
          <CoworkerHtmlPreview files={htmlFiles} canPublish={isOwner} taskId={isOwner ? workTaskId : null} />
        </View>
      ) : null}
      {!isUser && coworkerTaskId && leftoverHtmlArtifacts.length > 0 ? (
        <View style={COWORKER_ARTIFACTS_CONTAINER_STYLE(messageWidth)}>
          <CoworkerHtmlPreview
            canPublish={isOwner}
            taskId={workTaskId}
            artifacts={leftoverHtmlArtifacts}
          />
        </View>
      ) : null}
      {!isUser && coworkerTaskId
        && leftoverOtherArtifacts.length > 0
        && !(messageWithStates.files?.length > 0) ? (
        <View style={COWORKER_ARTIFACTS_CONTAINER_STYLE(messageWidth)}>
          <CoworkerArtifacts taskId={workTaskId} artifacts={leftoverOtherArtifacts} canPublish={isOwner} />
        </View>
      ) : null}
      {/* Restore-to-turn actions for settled Work replies. The component
          renders nothing mid-run. */}
      {!isUser && coworkerTaskId && !messageWithStates.isStreaming && !messageWithStates.isWaiting ? (
        <View style={COWORKER_ARTIFACTS_CONTAINER_STYLE(messageWidth)}>
          <WorkTurnActions
            taskId={workTaskId}
            conversationId={conversationId}
            message={messageWithStates}
          />
        </View>
      ) : null}
      {isUser && messageWithStates.isQueued && !coworkerTaskId ? (
        <View
          style={QUEUED_CAPTION_STYLE}
          accessibilityLabel={t('messages.queuedOffline') || "Message queued — will send when you're back online"}
        >
          <Icon name="clock" size={11} color={colors.text_color_tertiary} style={{ marginRight: 4 }} />
          <Text size="s" color="tertiary">
            {t('messages.queuedOffline') || "Will send when you're back online"}
          </Text>
        </View>
      ) : null}
    </View>
  );
}

const QUEUED_CAPTION_STYLE = {
  flexDirection: 'row',
  alignItems: 'center',
  alignSelf: 'flex-end',
  marginTop: 2,
  paddingHorizontal: 4,
};

// Add a custom comparison function for React.memo
function arePropsEqual(prevProps, nextProps) {
  // Only re-render if these specific properties changed
  return (
    prevProps.conversationId === nextProps.conversationId &&
    prevProps.isOwner === nextProps.isOwner &&
    prevProps.message.reactions?.user === nextProps.message.reactions?.user &&
    prevProps.message.reactions?.agent === nextProps.message.reactions?.agent &&
    prevProps.isLastMessage === nextProps.isLastMessage &&
    prevProps.isInMessageGroup === nextProps.isInMessageGroup &&
    prevProps.isLastGroupInChat === nextProps.isLastGroupInChat &&
    prevProps.messageWidth === nextProps.messageWidth &&
    prevProps.showMessageActions === nextProps.showMessageActions &&
    prevProps.message.id === nextProps.message.id &&
    prevProps.message.role === nextProps.message.role &&
    prevProps.message.content === nextProps.message.content &&
    prevProps.message.thinkingContent === nextProps.message.thinkingContent &&
    prevProps.message.isTyping === nextProps.message.isTyping &&
    prevProps.message.isThinking === nextProps.message.isThinking &&
    prevProps.message.isWaiting === nextProps.message.isWaiting &&
    prevProps.message.isStreaming === nextProps.message.isStreaming &&
    prevProps.message.isQueued === nextProps.message.isQueued &&
    prevProps.message.isError === nextProps.message.isError &&
    prevProps.message.hasError === nextProps.message.hasError &&
    prevProps.message.isTruncated === nextProps.message.isTruncated &&
    prevProps.message.inferenceStatus === nextProps.message.inferenceStatus &&
    prevProps.message.inferenceStage === nextProps.message.inferenceStage &&
    prevProps.message.flags?.isSaved === nextProps.message.flags?.isSaved &&
    prevProps.message.isEdited === nextProps.message.isEdited &&
    prevProps.message.isArchivedMessage === nextProps.message.isArchivedMessage &&
    prevProps.message.flags?.isDeleted === nextProps.message.flags?.isDeleted &&
    prevProps.message.flags?.isGroupedResponsePreferred === nextProps.message.flags?.isGroupedResponsePreferred &&
    prevProps.message.flags?.hasSelectedPreferenceLocally === nextProps.message.flags?.hasSelectedPreferenceLocally &&
    // Store updates always replace these arrays wholesale (spread into a new
    // message object — never mutated in place), so reference equality is
    // sufficient and avoids running JSON.stringify on every comparison.
    prevProps.message.images === nextProps.message.images &&
    prevProps.message.parameters?.coworkerArtifacts === nextProps.message.parameters?.coworkerArtifacts &&
    prevProps.message.isRequestingMedia === nextProps.message.isRequestingMedia &&
    prevProps.message.buttons === nextProps.message.buttons &&
    prevProps.message.citations === nextProps.message.citations &&
    prevProps.message.products === nextProps.message.products &&
    prevProps.message.stockQuotes === nextProps.message.stockQuotes &&
    prevProps.message.places === nextProps.message.places &&
    prevProps.message.map === nextProps.message.map &&
    prevProps.message.workActions === nextProps.message.workActions &&
    prevProps.message.modelIds === nextProps.message.modelIds &&
    prevProps.message.parameters?.___displayModelIds === nextProps.message.parameters?.___displayModelIds &&
    prevProps.message.agentIds === nextProps.message.agentIds &&
    prevProps.message.inferences === nextProps.message.inferences &&
    prevProps.message.files === nextProps.message.files &&
    prevProps.message.parentMessageId === nextProps.message.parentMessageId
  );
}

Message.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string,
    parentMessageId: PropTypes.string,
    thinkingContent: PropTypes.string,
    content: PropTypes.string,
    isTyping: PropTypes.bool,
    isThinking: PropTypes.bool,
    isWaiting: PropTypes.bool,
    isStreaming: PropTypes.bool,
    isError: PropTypes.bool,
    hasError: PropTypes.bool,
    inferenceStatus: PropTypes.string, // Human-readable status message (e.g., "Searching the web...")
    inferenceStage: PropTypes.string, // Processing stage (start, intent, layer1, preprocessing, preparation, execution, extraction)
    buttons: PropTypes.array,
    citations: PropTypes.array,
    inferences: PropTypes.array,
    isRequestingMedia: PropTypes.string, // image, video, audio, pdf, etc
    workActions: PropTypes.oneOf(['chrome', 'final']),
    modelIds: PropTypes.arrayOf(PropTypes.string),
    agentIds: PropTypes.arrayOf(PropTypes.string),
    images: PropTypes.arrayOf(PropTypes.shape({
      url: PropTypes.string,
      flags: PropTypes.shape({
        isExpired: PropTypes.bool
      })
    }))
  }).isRequired,
  conversationId: PropTypes.string.isRequired,
  isLastMessage: PropTypes.bool,
  isInMessageGroup: PropTypes.bool,
};

Message.displayName = 'Message';

export default React.memo(Message, arePropsEqual);
